import { Box, Section } from "@vinomofo/components";

import BannerGroupLayout from "./page-section-layouts/banner-group";
import { ContentBlock } from "./content-block";
import HTMLBannerLayout from "./page-section-layouts/html-banner";
import HeroBannerLayout from "./page-section-layouts/full-banner";
import InlineBannerLayout from "./page-section-layouts/inline-banner";
import { PropsWithChildren } from "react";
import styled from "styled-components";

const paddingMap = {
  0: [0],
  1: [10, 15, 20],
  2: [20, 30, 40],
  3: [30, 45, 60],
  4: [40, 60, 80],
};

const getSpacing = (value?: number) =>
  value && value < 0 ? 0 : paddingMap[value];
const getMargin = (value?: number) => (value && value < 0 ? value : 0);

interface SectionWrapperProps {
  isBoxed: boolean;
  isEventCountdown: boolean;
  fullWidth?: boolean;
  spaceBefore?: number;
  spaceAfter?: number;
  backgroundColor?: string;
  backgroundImage?: {
    url: string;
  };
}

export const StyledBox = styled(Box)<{ bgColor?: string }>`
  &:has(> div:empty) {
    padding: 0 !important;
  }
  background-color: ${({ bgColor }) => bgColor};
`;

export const StyledSection = styled(Section)<{ bgColor?: string }>`
  &:has(> div:empty) {
    padding: 0 !important;
  }

  > div {
    position: relative;
    background-color: ${({ bgColor }) => bgColor};
    border-radius: ${({ bgColor }) => (bgColor ? "12px" : "none")};
    color: ${({ bgColor }) => (bgColor ? "white" : "none")};
    z-index: ${({ bgColor }) => (bgColor ? "2" : "inherit")};
  }
`;

export const Box_With_BackgroundImage = styled(Box)<{
  $backgroundImage: string;
  isEventCountdown: boolean;
}>`
  background: linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)),
    url(${({ $backgroundImage }) => $backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  ${({ isEventCountdown, theme }) =>
    isEventCountdown &&
    `
      @media ${theme.mq.max.mobile} {
        background-size: 82vh auto;
        background-position: top center;
        background-color: #000;
      }
    `}
`;

export const SectionWrapper = ({
  isEventCountdown,
  fullWidth,
  spaceBefore,
  spaceAfter,
  children,
  backgroundColor = "transparent",
  backgroundImage,
  isBoxed,
}: PropsWithChildren<SectionWrapperProps>): JSX.Element => {
  const commonProps = {
    pt: getSpacing(spaceBefore),
    pb: getSpacing(spaceAfter),
    mt: getMargin(spaceBefore),
    mb: getMargin(spaceAfter),
    position: "relative" as const,
    maxWidth: isBoxed ? "1920px" : "100%",
  };

  const content = fullWidth ? (
    <StyledBox {...commonProps} bgColor={backgroundColor}>
      {children}
    </StyledBox>
  ) : (
    <StyledSection {...commonProps} bgColor={backgroundColor}>
      {children}
    </StyledSection>
  );
  return backgroundImage ? (
    <Box_With_BackgroundImage
      isEventCountdown={isEventCountdown}
      $backgroundImage={backgroundImage?.url}
      mt={getMargin(spaceBefore)}
      mb={getMargin(spaceAfter)}
      position={"relative"}
      maxWidth={isBoxed ? "1920px" : "100%"}
    >
      {content}
    </Box_With_BackgroundImage>
  ) : (
    content
  );
};

export const SectionContent = (data) => {
  const { pageSectionIndex, pageId, contentBlocks, layout } = data;
  switch (layout) {
    case "Banner Group":
      return BannerGroupLayout.create({ contentBlocks });
    case "Hero Banner":
      return HeroBannerLayout.create({ banner: contentBlocks[0] });
    case "HTML Banner":
      return HTMLBannerLayout.create({ banner: contentBlocks[0] });
    case "Inline Banner":
      return InlineBannerLayout.create({
        banner: contentBlocks[0],
        ...data,
      });
    default:
      return contentBlocks.map((contentBlock, index) => {
        return (
          <ContentBlock
            {...contentBlock}
            pageId={pageId}
            layout={layout}
            key={`content-block-${index}`}
            pageSectionIndex={pageSectionIndex}
            hero={index === 0}
          />
        );
      });
  }
};

const create = (data, index) => {
  const {
    pageId,
    fullWidth,
    spaceBefore,
    spaceAfter,
    backgroundColor,
    backgroundImage,
    contentBlocks,
  } = data;
  const isEventCountdown =
    contentBlocks[0] &&
    contentBlocks[0].__typename === "VinoData_EventCountdown";

  const isBoxed = !contentBlocks.some(
    (block) =>
      block.__typename === "VinoData_StaticBlock" &&
      ["Social Links", "Divider Brush"].includes(block.staticType)
  );

  const isSingleProductCollection =
    contentBlocks.length === 1 &&
    contentBlocks[0].__typename === "VinoData_ProductCollection";

  const wrapperProps: SectionWrapperProps = {
    fullWidth,
    spaceBefore: !spaceBefore && isSingleProductCollection ? 2 : spaceBefore,
    spaceAfter: !spaceAfter && isSingleProductCollection ? 2 : spaceAfter,
    backgroundColor,
    backgroundImage,
    isEventCountdown: isEventCountdown,
    isBoxed,
  };

  return (
    <SectionWrapper {...wrapperProps} key={index}>
      <SectionContent {...data} pageId={pageId} pageSectionIndex={index} />
    </SectionWrapper>
  );
};

const test = (data): boolean => data._type === "pageSection";

export default { test, create };
